import { useCallback, useMemo, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import { useToast } from '../../../hooks/useToast'
import MobileReplenishmentCard from './MobileReplenishmentCard'
import {
  painelBucket, painelKpis, PAINEL_BUCKET_META, RUPTURA_META_PCT, type PainelBucket,
} from '../../../lib/painelBucket'
import type { ProductRow } from '../../../types'

interface Props {
  rows: ProductRow[]
  supplierOptions: string[]
}

// Filtro unificado (chips + cards KPI). 'a_repor' = ruptura + priorizar.
type Filter = 'todos' | PainelBucket | 'a_repor'

// Chips de filtro por bucket (fora "Todos"). Saudável fica no card Cobertura.
const CHIP_BUCKETS: PainelBucket[] = ['ruptura', 'priorizar', 'aprovada']

function matchesFilter(b: PainelBucket, f: Filter): boolean {
  if (f === 'todos') return true
  if (f === 'a_repor') return b === 'ruptura' || b === 'priorizar'
  return b === f
}

export default function MobileReplenishmentList({ rows, supplierOptions }: Props) {
  const { notify } = useToast()
  const [search, setSearch] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [filter, setFilter] = useState<Filter>('todos')
  const [inactivatingId, setInactivatingId] = useState<string | null>(null)

  const bucketOf = useCallback((r: ProductRow) => painelBucket({
    priority: r.priority, kanban_status: r.kanban_status, current_stock: r.current_stock, em_risco: r.em_risco,
  }), [])

  const kpis = useMemo(() => painelKpis(rows), [rows])

  const counts = useMemo(() => {
    const c: Record<PainelBucket, number> = { ruptura: 0, priorizar: 0, aprovada: 0, saudavel: 0 }
    for (const r of rows) c[bucketOf(r)]++
    return c
  }, [rows, bucketOf])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(r => {
      if (q && !r.sku.toLowerCase().includes(q) && !r.name.toLowerCase().includes(q)) return false
      if (supplierFilter && r.suppliers?.name !== supplierFilter) return false
      if (filter !== 'todos' && !matchesFilter(bucketOf(r), filter)) return false
      return true
    })
  }, [rows, search, supplierFilter, filter, bucketOf])

  // Alterna o filtro: tocar no que já está ativo volta para "todos".
  const toggle = useCallback((f: Filter) => setFilter(prev => (prev === f ? 'todos' : f)), [])

  const handleInactivate = useCallback(async (id: string, name: string) => {
    if (!confirm(`Inativar "${name}"?\n\nO produto será removido do Painel e irá para Inativos.`)) return
    setInactivatingId(id)
    const { error } = await supabase.from('products').update({ is_active: false }).eq('id', id)
    setInactivatingId(null)
    if (error) notify('error', `Erro: ${error.message}`)
    else notify('success', `"${name}" inativado.`)
  }, [notify])

  return (
    <div className="space-y-3">
      {/* Subheader */}
      <p className="text-xs text-ink-faint">Reposição · hoje</p>

      {/* KPIs (cards touch — filtram a lista) */}
      <div className="grid grid-cols-3 gap-2">
        <Kpi
          label="Rupturas"
          value={kpis.rupturas}
          tone={kpis.rupturaPct > RUPTURA_META_PCT ? 'danger' : 'ink'}
          sub={`${kpis.rupturaPct.toLocaleString('pt-BR')}% · meta ${RUPTURA_META_PCT}%`}
          subTone={kpis.rupturaPct > RUPTURA_META_PCT ? 'danger' : 'ok'}
          active={filter === 'ruptura'}
          onClick={() => toggle('ruptura')}
        />
        <Kpi
          label="A repor"
          value={kpis.aRepor}
          tone="warning"
          active={filter === 'a_repor'}
          onClick={() => toggle('a_repor')}
        />
        <Kpi
          label="Cobertura"
          value={`${kpis.coberturaMedia}d`}
          tone="ink"
          sub="ver saudáveis"
          active={filter === 'saudavel'}
          onClick={() => toggle('saudavel')}
        />
      </div>

      {/* Chips semânticos — quebram linha, sem scroll horizontal */}
      <div className="flex flex-wrap gap-2">
        <ChipButton
          active={filter === 'todos'}
          onClick={() => setFilter('todos')}
          className={filter === 'todos' ? 'bg-accent text-brand-900 border-accent' : 'bg-surface text-ink-muted border-line'}
        >
          Todos <span className="tabular-nums opacity-80">{rows.length}</span>
        </ChipButton>
        {CHIP_BUCKETS.map(b => {
          const active = filter === b
          const m = PAINEL_BUCKET_META[b]
          return (
            <ChipButton
              key={b}
              active={active}
              onClick={() => toggle(b)}
              className={`bg-surface border-line ${active ? 'text-ink' : 'text-ink-muted'}`}
            >
              <span className={`w-2 h-2 rounded-full ${m.dot}`} />
              {m.label} <span className="tabular-nums opacity-80">{counts[b]}</span>
            </ChipButton>
          )
        })}
      </div>

      {/* Busca + fornecedor (linhas próprias, sem corte).
          Inputs com text-base (16px) para o iOS Safari não dar zoom ao focar. */}
      <div className="space-y-2">
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar SKU ou produto…"
          className="w-full h-10 bg-paper border border-line rounded-xl px-3 text-ink text-base placeholder:text-ink-faint"
        />
        {supplierOptions.length > 0 && (
          <SupplierPicker value={supplierFilter} options={supplierOptions} onChange={setSupplierFilter} />
        )}
      </div>

      {/* Contador */}
      <p className="text-xs text-ink-faint text-right">
        {filtered.length === rows.length ? `${rows.length} itens` : `${filtered.length} de ${rows.length}`}
      </p>

      {/* Cards */}
      <div className="space-y-2.5">
        {filtered.length === 0 ? (
          <div className="py-16 text-center text-ink-faint">
            <p className="text-2xl mb-2">🔍</p>
            <p>Nenhum item encontrado.</p>
          </div>
        ) : filtered.map(r => (
          <MobileReplenishmentCard
            key={r.id}
            row={r}
            onInactivate={handleInactivate}
            inactivating={inactivatingId === r.id}
          />
        ))}
      </div>
    </div>
  )
}

function Kpi({ label, value, tone, sub, subTone, active, onClick }: {
  label: string; value: string | number; tone: 'ink' | 'danger' | 'warning'
  sub?: string; subTone?: 'ok' | 'danger'; active?: boolean; onClick?: () => void
}) {
  const color = tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-[#b35f00] dark:text-amber-300' : 'text-ink'
  const subColor = subTone === 'danger' ? 'text-danger' : subTone === 'ok' ? 'text-emerald-600 dark:text-emerald-400' : 'text-ink-faint'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border bg-surface px-3 py-2.5 touch-action-manip transition-all active:bg-sunken/40 ${active ? 'border-accent ring-2 ring-accent/40' : 'border-line'}`}
    >
      <p className={`text-2xl font-bold tabular-nums leading-tight ${color}`}>{value}</p>
      <p className="text-[11px] text-ink-muted leading-tight">{label}</p>
      {sub && <p className={`text-[10px] font-semibold tabular-nums leading-tight mt-0.5 ${subColor}`}>{sub}</p>}
    </button>
  )
}

function ChipButton({ active, onClick, className, children }: {
  active: boolean; onClick: () => void; className?: string; children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 h-9 px-3.5 rounded-full border text-xs font-semibold flex items-center gap-1.5 touch-action-manip transition-all ${active ? 'ring-2 ring-ink/15' : ''} ${className ?? ''}`}
    >
      {children}
    </button>
  )
}

/** Combobox de fornecedor com busca por digitação. */
function SupplierPicker({ value, options, onChange }: {
  value: string; options: string[]; onChange: (v: string) => void
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const base = q ? options.filter(o => o.toLowerCase().includes(q)) : options
    return base.slice(0, 60)
  }, [query, options])

  function pick(v: string) {
    onChange(v)
    setQuery('')
    setOpen(false)
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={open ? query : value}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => { setOpen(true); setQuery('') }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Fornecedor — digite para buscar…"
        aria-label="Filtrar por fornecedor"
        className="w-full h-10 bg-paper border border-line rounded-xl pl-3 pr-8 text-ink text-base placeholder:text-ink-faint"
      />
      {value && (
        <button
          type="button"
          aria-label="Limpar fornecedor"
          onMouseDown={e => { e.preventDefault(); pick('') }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center text-ink-faint hover:text-ink text-lg leading-none"
        >×</button>
      )}
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-xl border border-line bg-surface shadow-lg">
          <button
            type="button"
            onMouseDown={e => { e.preventDefault(); pick('') }}
            className="w-full text-left px-3 py-2 text-sm text-ink-muted active:bg-sunken hover:bg-sunken"
          >Todos os fornecedores</button>
          {filtered.map(o => (
            <button
              key={o}
              type="button"
              onMouseDown={e => { e.preventDefault(); pick(o) }}
              className={`w-full text-left px-3 py-2 text-sm truncate active:bg-sunken hover:bg-sunken ${o === value ? 'text-accent font-semibold' : 'text-ink'}`}
            >{o}</button>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-sm text-ink-faint">Nenhum fornecedor encontrado.</p>
          )}
        </div>
      )}
    </div>
  )
}
